import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  type UploadTaskSnapshot
} from 'firebase/storage'
import { storage as _storage } from './config'
import type { FirebaseStorage } from 'firebase/storage'
import type { ApiResponse } from '@/types'

const getStorage = (): FirebaseStorage => {
  if (!_storage) throw new Error('Firebase is not configured. Please set up your .env credentials.')
  return _storage
}

const storage = new Proxy({} as FirebaseStorage, {
  get(_target, prop) {
    return (getStorage() as unknown as Record<string | symbol, unknown>)[prop]
  }
})

class StorageService {
  /**
   * Upload file with progress tracking
   */
  async uploadFile(
    path: string,
    file: File | Blob,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<string>> {
    try {
      const storageRef = ref(storage, path)
      
      if (onProgress) {
        // Use resumable upload for progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file)
        
        return new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              onProgress(progress)
            },
            (error) => {
              reject({ success: false, error: error.message })
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
              resolve({ success: true, data: downloadURL })
            }
          )
        })
      } else {
        // Simple upload without progress
        const snapshot = await uploadBytes(storageRef, file)
        const downloadURL = await getDownloadURL(snapshot.ref)
        return { success: true, data: downloadURL }
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Upload failed'
      }
    }
  }

  /**
   * Upload base64 image
   */
  async uploadBase64Image(
    path: string,
    base64String: string,
    contentType = 'image/png'
  ): Promise<ApiResponse<string>> {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(base64String.split(',')[1] || base64String)
      const byteNumbers = new Array(byteCharacters.length)
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: contentType })
      
      return await this.uploadFile(path, blob)
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to upload image'
      }
    }
  }

  /**
   * Get download URL
   */
  async getFileUrl(path: string): Promise<ApiResponse<string>> {
    try {
      const storageRef = ref(storage, path)
      const url = await getDownloadURL(storageRef)
      return { success: true, data: url }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get file URL'
      }
    }
  }

  /**
   * Delete file
   */
  async deleteFile(path: string): Promise<ApiResponse<void>> {
    try {
      const storageRef = ref(storage, path)
      await deleteObject(storageRef)
      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete file'
      }
    }
  }

  /**
   * List files in directory
   */
  async listFiles(path: string): Promise<ApiResponse<string[]>> {
    try {
      const storageRef = ref(storage, path)
      const result = await listAll(storageRef)
      const urls = await Promise.all(
        result.items.map(itemRef => getDownloadURL(itemRef))
      )
      return { success: true, data: urls }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to list files'
      }
    }
  }

  /**
   * Generate unique file path
   */
  generateFilePath(
    basePath: string,
    fileName?: string,
    extension?: string
  ): string {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 8)
    const ext = extension || (fileName ? fileName.split('.').pop() : 'jpg')
    const name = fileName ? fileName.split('.')[0] : 'file'
    
    return `${basePath}/${name}_${timestamp}_${random}.${ext}`
  }

  /**
   * Compress image before upload
   */
  async compressImage(
    file: File,
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = URL.createObjectURL(file)
      
      img.onload = () => {
        let { width, height } = img
        
        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }
        
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }
        
        ctx.drawImage(img, 0, 0, width, height)
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to compress image'))
            }
          },
          'image/jpeg',
          quality
        )
      }
      
      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }
    })
  }
}

export const storageService = new StorageService()
export default storageService
